import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

export interface CanonicalTreeFamilyUnitRetentionInput {
  readonly members:
    readonly GraphMember[];
  readonly memberId: string;
}

export type CanonicalTreeFamilyUnitRetentionReason =
  | 'MEMBER_NOT_ACTIVE'
  | 'CANONICAL_PARENT'
  | 'CANONICAL_CHILD'
  | 'NONANCESTRAL_PARTNER';

export interface CanonicalTreeFamilyUnitRetentionDecision {
  readonly memberId: string;
  readonly retainTreeCard: boolean;
  readonly mayRenderFamilyUnitChip: boolean;
  readonly mayReplaceTreeCardWithChip: boolean;
  readonly reason:
    CanonicalTreeFamilyUnitRetentionReason;
  readonly canonicalParentOfMemberIds:
    readonly string[];
  readonly canonicalParentIds:
    readonly string[];
}

const uniqueIds = (
  values: readonly string[],
): string[] =>
  Array.from(
    new Set(
      values.filter(Boolean),
    ),
  ).sort();

const activeMembers = (
  members: readonly GraphMember[],
): readonly GraphMember[] =>
  members.filter(
    member =>
      member.approvalStatus !== 'rejected' &&
      member.profileState !== 'deleted',
  );

export const resolveCanonicalTreeFamilyUnitRetention = (
  input:
    CanonicalTreeFamilyUnitRetentionInput,
): CanonicalTreeFamilyUnitRetentionDecision => {
  const active =
    activeMembers(input.members);

  const member =
    active.find(
      candidate =>
        candidate.id === input.memberId,
    );

  if (!member) {
    return Object.freeze({
      memberId:
        input.memberId,
      retainTreeCard:
        false,
      mayRenderFamilyUnitChip:
        false,
      mayReplaceTreeCardWithChip:
        false,
      reason:
        'MEMBER_NOT_ACTIVE',
      canonicalParentOfMemberIds:
        Object.freeze([]),
      canonicalParentIds:
        Object.freeze([]),
    });
  }

  const activeIds =
    new Set(
      active.map(
        candidate =>
          candidate.id,
      ),
    );

  const canonicalParentIds =
    uniqueIds(
      (member.parentIds ?? []).filter(
        parentId =>
          activeIds.has(parentId),
      ),
    );

  const canonicalParentOfMemberIds =
    uniqueIds(
      active
        .filter(
          candidate =>
            (
              candidate.parentIds ??
              []
            ).includes(member.id),
        )
        .map(
          candidate =>
            candidate.id,
        ),
    );

  if (
    canonicalParentOfMemberIds.length > 0
  ) {
    return Object.freeze({
      memberId:
        member.id,
      retainTreeCard:
        true,
      mayRenderFamilyUnitChip:
        false,
      mayReplaceTreeCardWithChip:
        false,
      reason:
        'CANONICAL_PARENT',
      canonicalParentOfMemberIds:
        Object.freeze(
          canonicalParentOfMemberIds,
        ),
      canonicalParentIds:
        Object.freeze(
          canonicalParentIds,
        ),
    });
  }

  if (
    canonicalParentIds.length > 0
  ) {
    return Object.freeze({
      memberId:
        member.id,
      retainTreeCard:
        true,
      mayRenderFamilyUnitChip:
        false,
      mayReplaceTreeCardWithChip:
        false,
      reason:
        'CANONICAL_CHILD',
      canonicalParentOfMemberIds:
        Object.freeze(
          canonicalParentOfMemberIds,
        ),
      canonicalParentIds:
        Object.freeze(
          canonicalParentIds,
        ),
    });
  }

  return Object.freeze({
    memberId:
      member.id,
    retainTreeCard:
      false,
    mayRenderFamilyUnitChip:
      true,
    mayReplaceTreeCardWithChip:
      true,
    reason:
      'NONANCESTRAL_PARTNER',
    canonicalParentOfMemberIds:
      Object.freeze([]),
    canonicalParentIds:
      Object.freeze([]),
  });
};
